import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import SharedPageHeader from "@/components/SharedPageHeader";
import OrdersTable from "@/components/OrdersTable";
import OrdersFilters from "@/components/OrdersFilters";
import DemoBanner from "@/components/DemoBanner";
import { useEtsyStore } from "@/hooks/useEtsyStore";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { EtsyOrder } from "@/hooks/useEtsyOrders";
import { setCachedOrders } from "@/utils/etsyCache";

const Orders = () => {
  const {
    isConnected,
    orders,
    isFetchingOrders,
    fetchOrders,
    checkConnection,
  } = useEtsyStore();
  
  const { hasActiveSubscription } = useSubscriptionStatus();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState<EtsyOrder[]>([]);
  const [localOrders, setLocalOrders] = useState<EtsyOrder[]>([]);

  // Update local orders when store orders change
  useEffect(() => {
    setLocalOrders(orders);
  }, [orders]);

  // Update filtered orders when local orders change
  useEffect(() => {
    setFilteredOrders(localOrders);
  }, [localOrders]);

  // Apply search filter to the already filtered orders
  const searchFilteredOrders = filteredOrders.filter((order: EtsyOrder) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      order.receiptId.toLowerCase().includes(searchLower) ||
      order.customerName.toLowerCase().includes(searchLower) ||
      order.productName.toLowerCase().includes(searchLower) ||
      order.city.toLowerCase().includes(searchLower) ||
      order.status.toLowerCase().includes(searchLower) ||
      (order.customerEmail && order.customerEmail.toLowerCase().includes(searchLower))
    );
  });

  const handleConnectionChange = async (connected: boolean) => {
    if (!connected) {
      await checkConnection(true); // Force refresh
    }
  };

  const handleOrderUpdate = (updatedOrder: EtsyOrder) => {
    console.log("Updating order:", updatedOrder);
    const updatedOrders = localOrders.map(order => 
      order.receiptId === updatedOrder.receiptId ? updatedOrder : order
    );
    console.log("Updated orders:", updatedOrders);
    setLocalOrders(updatedOrders);
    setCachedOrders(updatedOrders); // <-- ADD THIS line to also save to localStorage
  };

  const handleOrderAdd = (newOrder: EtsyOrder) => {
    const updatedOrders = [newOrder, ...localOrders];
    setLocalOrders(updatedOrders);
    setCachedOrders(updatedOrders); // <-- Optionally update cache here too for adds
  };

  return (
    <DashboardLayout>
      <SharedPageHeader
        title="Manage Orders"
        description="Track and manage all your Etsy orders in one place."
        feature="order management"
        isConnected={isConnected}
        onConnectionChange={handleConnectionChange}
      >
        {!hasActiveSubscription && (
          <DemoBanner className="mb-6" />
        )}

        {isConnected && (
          <OrdersFilters 
            searchTerm={searchTerm} 
            onSearchChange={setSearchTerm}
            orders={localOrders}
            onFiltersChange={setFilteredOrders}
            onOrderAdd={handleOrderAdd}
          />
        )}

        <OrdersTable 
          orders={searchFilteredOrders}
          totalOrders={localOrders.length}
          isConnected={isConnected}
          isFetchingOrders={isFetchingOrders}
          onOrderUpdate={handleOrderUpdate}
        />
      </SharedPageHeader>
    </DashboardLayout>
  );
};

export default Orders;
